/**
 * SeeMe Combobox
 * Select + typeahead hybrid with optional "Add New" action.
 *
 * Builds on the dropdown system (dropdowns.js must be loaded first).
 * Enhances [data-combobox] containers with search filtering, keyboard
 * navigation, selection display, and an optional "add new" callback.
 *
 * Markup:
 *   <div class="dropdown typeahead" data-combobox>
 *     <button class="combobox-trigger" data-dropdown>
 *       <span class="combobox-value" data-combobox-value>Select...</span>
 *       <i class="fa-solid fa-chevron-down"></i>
 *     </button>
 *     <div class="dropdown-menu">
 *       <div class="combobox-search">
 *         <input class="form-control form-control-sm typeahead-input"
 *                placeholder="Search..." data-combobox-search>
 *       </div>
 *       <div class="typeahead-results show" data-combobox-list>
 *         <a class="dropdown-item" data-combobox-option="val1" href="#">Option 1</a>
 *         <a class="dropdown-item" data-combobox-option="val2" href="#">Option 2</a>
 *       </div>
 *       <div class="combobox-empty">No results found</div>
 *       <hr class="dropdown-divider combobox-footer-divider">
 *       <a class="dropdown-item combobox-footer" href="#" data-combobox-add>
 *         <i class="fa-solid fa-plus"></i> Add new...
 *       </a>
 *     </div>
 *   </div>
 *
 * Events (dispatched on the [data-combobox] element):
 *   combobox:select  — detail: { value, label, option }
 *                      (an X clear sends value '', label '', option null)
 *   combobox:add     — detail: { query }  (current search text)
 *
 * Option label:
 *   The trigger shows the option's text. An option that carries extra content
 *   (badges, a trade, a market) sets data-combobox-label to the plain label
 *   for the trigger; search still matches the option's full text.
 *
 * Hidden input:
 *   If a <input type="hidden" data-combobox-input> exists inside the
 *   container, its value is updated on selection.
 *
 * Clearable:
 *   data-combobox-clearable on the container adds a .btn-dismiss X right
 *   after the trigger, shown while the field has a value (the hidden input's
 *   value, or a non-placeholder trigger when there is no hidden input) and
 *   the trigger is not disabled.
 *   Clicking it empties the hidden input, restores the placeholder and fires
 *   combobox:select with value '' and option null, without opening the menu.
 *   A field rendered with a value has no placeholder text to remember, so set
 *   data-combobox-placeholder on the container to the text it should show.
 *   The button is added on DOMContentLoaded, htmx:afterSettle and sheet:ready.
 *
 * Open:
 *   On dropdown:open the search is cleared, the list re-filtered, the menu's
 *   width locked, and the search focused synchronously so iOS raises the
 *   keyboard.  On phones dropdowns.js portals the menu to body as a sheet;
 *   ownerOf / findIn / findAllIn follow its _dropdownOwner / _portaledMenu
 *   links.
 */

(function () {
    'use strict';

    // dropdowns.js portals a sheet-mode menu to body and links both ways:
    // menu._dropdownOwner is the .dropdown wrapper, wrapper._portaledMenu the
    // menu.  ownerOf looks up and findIn / findAllIn look down across that gap
    // (defined in dropdowns.js, shared through window._cc).
    var ownerOf = window._cc.ownerOf;
    var findIn = window._cc.findIn;
    var findAllIn = window._cc.findAllIn;

    // Remember the placeholder text: data-combobox-placeholder if set, else the
    // trigger's text when it renders empty, else 'Select...'.  Runs on first
    // open, and at load for clearable fields.
    function ensurePlaceholder(combobox) {
        if (combobox._comboboxPlaceholder) return;
        var attr = combobox.getAttribute('data-combobox-placeholder');
        if (attr) {
            combobox._comboboxPlaceholder = attr;
            return;
        }
        var valueEl = findIn(combobox, '[data-combobox-value]');
        if (valueEl && valueEl.classList.contains('combobox-value--placeholder')) {
            combobox._comboboxPlaceholder = valueEl.textContent.trim();
        } else {
            combobox._comboboxPlaceholder = 'Select...';
        }
    }

    // Open: clear filter, lock width, focus search.  dropdown:open fires inside
    // the user's click after the menu is placed, so the focus raises the iOS
    // keyboard.
    document.addEventListener('dropdown:open', function (e) {
        var menu = e.detail && e.detail.menu;
        var combobox = menu && ownerOf(menu, '[data-combobox]');
        if (!combobox) return;
        ensurePlaceholder(combobox);
        var search = menu.querySelector('[data-combobox-search]');
        if (!search) return;
        search.value = '';
        filterItems(combobox, '');
        // A phone sheet spans the screen; locking that width would carry a stale
        // minWidth into the next desktop open after a resize closes the sheet.
        if (!e.detail.sheet) menu.style.minWidth = menu.offsetWidth + 'px';
        search.focus({ preventScroll: true });
    });

    // Close: clear locked width
    document.addEventListener('click', function (e) {
        document.querySelectorAll('[data-combobox] .dropdown-menu').forEach(function (menu) {
            if (!menu.classList.contains('show')) {
                menu.style.minWidth = '';
            }
        });
    });

    // Filter option items based on query
    function filterItems(combobox, query) {
        var list = findIn(combobox, '[data-combobox-list]');
        var emptyMsg = findIn(combobox, '.combobox-empty');
        if (!list) return;

        var items = list.querySelectorAll('[data-combobox-option]');
        var q = query.toLowerCase().trim();
        var visibleCount = 0;

        items.forEach(function (item) {
            var text = item.textContent.toLowerCase();
            var match = !q || text.indexOf(q) !== -1;
            item.style.display = match ? '' : 'none';
            if (match) visibleCount++;
        });

        // Show/hide "no results" message
        if (emptyMsg) {
            emptyMsg.classList.toggle('show', visibleCount === 0 && q.length > 0);
        }

        // Show "Add new" only when there's a search query
        var addBtn = findIn(combobox, '[data-combobox-add]');
        var addDivider = findIn(combobox, '.combobox-footer-divider');
        if (addBtn) {
            if (q) {
                addBtn.style.display = '';
                if (addDivider) addDivider.style.display = '';
                addBtn.innerHTML = '<i class="fa-solid fa-plus"></i> Add "' + escapeHtml(query.trim()) + '"';
            } else {
                addBtn.style.display = 'none';
                if (addDivider) addDivider.style.display = 'none';
            }
        }

        // Reset keyboard focus
        clearFocus(combobox);
    }

    // Keyboard navigation state
    function getVisibleItems(combobox) {
        var list = findIn(combobox, '[data-combobox-list]');
        if (!list) return [];
        return Array.prototype.filter.call(
            list.querySelectorAll('[data-combobox-option]'),
            function (el) { return el.style.display !== 'none'; }
        );
    }

    function clearFocus(combobox) {
        findAllIn(combobox, '.dropdown-item.focused').forEach(function (el) {
            el.classList.remove('focused');
        });
    }

    function escapeHtml(str) {
        var div = document.createElement('div');
        div.appendChild(document.createTextNode(str));
        return div.innerHTML;
    }

    // Select an option
    function selectOption(combobox, option) {
        var value = option.getAttribute('data-combobox-option');
        var label = option.getAttribute('data-combobox-label') || option.textContent.trim();
        applySelection(combobox, value, label, option);
    }

    // Update the trigger, hidden input and selected mark, then fire
    // combobox:select.  option is null for a clear from the X button.
    function applySelection(combobox, value, label, option) {
        // Update trigger display
        var valueEl = findIn(combobox, '[data-combobox-value]');
        if (valueEl) {
            if (!value) {
                valueEl.textContent = combobox._comboboxPlaceholder || 'Select...';
                valueEl.classList.add('combobox-value--placeholder');
            } else {
                valueEl.textContent = label;
                valueEl.classList.remove('combobox-value--placeholder');
            }
        }

        // Update hidden input
        var hiddenInput = findIn(combobox, '[data-combobox-input]');
        if (hiddenInput) {
            hiddenInput.value = value;
        }

        // Mark selected
        findAllIn(combobox, '[data-combobox-option]').forEach(function (el) {
            el.removeAttribute('aria-selected');
        });
        if (option) option.setAttribute('aria-selected', 'true');

        // Dispatch event
        combobox.dispatchEvent(new CustomEvent('combobox:select', {
            bubbles: true,
            detail: { value: value, label: label, option: option }
        }));
    }

    // ── Clearable ───────────────────────────────────────────────────────

    function hasValue(combobox) {
        var hiddenInput = findIn(combobox, '[data-combobox-input]');
        if (hiddenInput) return !!hiddenInput.value;
        var valueEl = findIn(combobox, '[data-combobox-value]');
        return !!valueEl && !valueEl.classList.contains('combobox-value--placeholder');
    }

    function isDisabled(combobox) {
        var trigger = combobox.querySelector(':scope > .combobox-trigger');
        return !trigger || trigger.disabled;
    }

    function syncClear(combobox) {
        var btn = combobox.querySelector(':scope > [data-combobox-clear]');
        if (btn) btn.hidden = isDisabled(combobox) || !hasValue(combobox);
    }

    // The X sits beside the trigger, not inside it: the trigger is a <button>.
    function initClearable(root) {
        var list = Array.prototype.slice.call(root.querySelectorAll('[data-combobox-clearable]'));
        if (root.matches && root.matches('[data-combobox-clearable]')) list.push(root);
        list.forEach(function (combobox) {
            if (combobox.querySelector(':scope > [data-combobox-clear]')) return;
            var trigger = combobox.querySelector(':scope > .combobox-trigger');
            if (!trigger) return;
            ensurePlaceholder(combobox);
            var btn = document.createElement('button');
            btn.type = 'button';
            btn.className = 'btn-dismiss combobox-clear';
            btn.setAttribute('data-combobox-clear', '');
            btn.setAttribute('aria-label', 'Clear');
            btn.innerHTML = '<i class="fa-solid fa-xmark"></i>';
            trigger.after(btn);
            syncClear(combobox);
        });
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function () { initClearable(document); });
    } else {
        initClearable(document);
    }
    document.addEventListener('htmx:afterSettle', function (e) {
        if (e.target && e.target.querySelectorAll) initClearable(e.target);
    });
    // Script-rendered sheets (Houses' sheets.js) fire sheet:ready on the sheet
    // instead of an htmx swap.
    document.addEventListener('sheet:ready', function (e) {
        if (e.target && e.target.querySelectorAll) initClearable(e.target);
    });

    // Every selection, including ones fired by other scripts, re-syncs the X.
    document.addEventListener('combobox:select', function (e) {
        var combobox = e.target.closest && e.target.closest('[data-combobox-clearable]');
        if (combobox) syncClear(combobox);
    });

    document.addEventListener('click', function (e) {
        var btn = e.target.closest('[data-combobox-clear]');
        if (!btn) return;
        var combobox = btn.closest('[data-combobox]');
        if (!combobox) return;
        e.preventDefault();
        if (isDisabled(combobox)) return;
        ensurePlaceholder(combobox);
        applySelection(combobox, '', '', null);
        var trigger = combobox.querySelector(':scope > .combobox-trigger');
        if (trigger) trigger.focus();
    });

    // Search input handler
    document.addEventListener('input', function (e) {
        if (!e.target.hasAttribute('data-combobox-search')) return;
        var combobox = ownerOf(e.target, '[data-combobox]');
        if (!combobox) return;
        filterItems(combobox, e.target.value);
    });

    // Option selection
    document.addEventListener('click', function (e) {
        var option = e.target.closest('[data-combobox-option]');
        if (!option) return;
        var combobox = ownerOf(option, '[data-combobox]');
        if (!combobox) return;

        e.preventDefault();
        selectOption(combobox, option);
        // Dropdown close is handled by dropdowns.js (click on .dropdown-item)
    });

    // "Add new" click
    document.addEventListener('click', function (e) {
        var addBtn = e.target.closest('[data-combobox-add]');
        if (!addBtn) return;
        var combobox = ownerOf(addBtn, '[data-combobox]');
        if (!combobox) return;

        e.preventDefault();
        var search = findIn(combobox, '[data-combobox-search]');
        var query = search ? search.value.trim() : '';

        combobox.dispatchEvent(new CustomEvent('combobox:add', {
            bubbles: true,
            detail: { query: query }
        }));
    });

    // Keyboard navigation inside search input
    document.addEventListener('keydown', function (e) {
        var search = e.target.closest('[data-combobox-search]');
        if (!search) return;
        var combobox = ownerOf(search, '[data-combobox]');
        if (!combobox) return;

        var items = getVisibleItems(combobox);
        if (!items.length) return;

        var focused = findIn(combobox, '.dropdown-item.focused');
        var idx = focused ? items.indexOf(focused) : -1;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            clearFocus(combobox);
            idx = idx < items.length - 1 ? idx + 1 : 0;
            items[idx].classList.add('focused');
            items[idx].scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            clearFocus(combobox);
            idx = idx > 0 ? idx - 1 : items.length - 1;
            items[idx].classList.add('focused');
            items[idx].scrollIntoView({ block: 'nearest' });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (focused) {
                selectOption(combobox, focused);
                // Let dropdowns.js handle close via its .dropdown-item click handler
                // so portal restore, inline style cleanup, and panel reset all run
                focused.click();
            }
        }
    });

    // Prevent search input from closing the dropdown
    document.addEventListener('click', function (e) {
        if (e.target.hasAttribute('data-combobox-search')) {
            e.stopPropagation();
        }
    }, true);

})();
